import { max, min } from 'date-fns';

import { DEFAULT_COLOR_VALUES } from '@/lib/constants/colors';
import { formatWallClockDate, formatWallClockShortDate } from '@/utils/date-formatters';

import { formatHours } from '../../../helpers/format-time-track.helper';
import {
  ApprovalConfirmCard,
  ApprovalConfirmCardHeader,
  ProjectColorDot,
  ProjectMetaRow,
  TranslateFn,
} from './approval-confirm-card';

/** Per-project rollup of the selected worklog entries (bulk worklog-level approval). */
export interface BulkProjectGroup {
  projectId: number;
  projectName: string;
  dates: Date[];
  totalHours: number;
  totalApprovedForInvoiceHours: number;
  totalApprovedForBillHours: number;
  count: number;
}

interface BulkProjectCardProps {
  group: BulkProjectGroup;
  ns: string;
  t: TranslateFn;
}

/** Card summarizing all selected worklog entries grouped under one project (bulk mode). */
export function BulkProjectCard({ group, ns, t }: BulkProjectCardProps) {
  const projectColor = DEFAULT_COLOR_VALUES[group.projectId % DEFAULT_COLOR_VALUES.length];
  // Dates are wall-clock `...Z` values; min/max compares timestamps correctly, and the
  // wall-clock formatters read UTC components so the rendered range isn't timezone-shifted.
  const minDate = min(group.dates);
  const maxDate = max(group.dates);
  const dateRange =
    minDate.getTime() === maxDate.getTime()
      ? formatWallClockDate(minDate)
      : `${formatWallClockShortDate(minDate)} – ${formatWallClockDate(maxDate)}`;

  return (
    <ApprovalConfirmCard>
      <ApprovalConfirmCardHeader
        marker={<ProjectColorDot color={projectColor} />}
        name={group.projectName}
      />
      <div className="flex flex-col gap-1.5">
        <ProjectMetaRow label={t(`${ns}.projectCard.dateRange` as Parameters<typeof t>[0])}>
          {dateRange}
        </ProjectMetaRow>
        <ProjectMetaRow label={t(`${ns}.projectCard.totalHours` as Parameters<typeof t>[0])}>
          {formatHours(group.totalHours)}
        </ProjectMetaRow>
        <ProjectMetaRow
          label={t(`${ns}.projectCard.approvedForInvoiceHours` as Parameters<typeof t>[0])}
        >
          {formatHours(group.totalApprovedForInvoiceHours)}
        </ProjectMetaRow>
        <ProjectMetaRow
          label={t(`${ns}.projectCard.approvedForBillHours` as Parameters<typeof t>[0])}
        >
          {formatHours(group.totalApprovedForBillHours)}
        </ProjectMetaRow>
        <ProjectMetaRow label={t(`${ns}.projectCard.entries` as Parameters<typeof t>[0])}>
          {group.count}
        </ProjectMetaRow>
      </div>
    </ApprovalConfirmCard>
  );
}
